import React from 'react';
import { useNavigate } from 'react-router-dom';
import { shouldSlide } from 'utils/pageTransition';
import { saveScrollPosition } from 'utils/scrollRestore';

import { CertificatesMenu } from 'components/UI/CertificatesMenu';
import { StatusBadge } from 'components/pages/certificates/StatusBadge';
import { STATUS_CHIP_HEIGHT } from 'components/pages/certificates/statusChipSx';
import { TagOpenedBadge } from 'components/pages/certificates/TagOpenedBadge';
import {
  ACTIVITY_STRIP_MIN_H,
  StatusNotification,
  hasActivityToShow,
} from 'components/pages/certificates/StatusNotification';
import { getCertificateStatusBadgeProps } from 'utils/displayStatusColorMapper';
import { formatSku } from 'utils/formatSku';

import { useSelector } from 'store/hooks';

import type { CertificateCustomer } from 'types';

import { Box, Collapse, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { SelectCheckbox } from 'components/UI/SelectCheckbox';
import { ProductImagePlaceholder } from '@repo/ui';
import { FadeInImage } from 'components/UI/FadeInImage';
import { edgeRadius, surfacePx, surfaceRadius } from 'settings/brandRadius';
import { isCertificateOwnedBy } from 'utils/certificateUtils';
import { DISPLAYED_DATA_DEFAULTS } from 'constants/certificateConstants';
interface CertificatesTeaserProps {
  /**
   * Two-per-row grid presentation: drops the activity block.
   *
   * Not cosmetic. The activity block is the card's whole height variance — when
   * grid was trialled with the full card, rows differed by up to 199px because
   * one card carried a three-line transfer notice and its neighbour carried
   * none, so the grid read as broken masonry rather than a grid. At half width
   * there is also nowhere for a three-line notice to go. The activity is one tap
   * away on the certificate itself.
   */
  compact?: boolean
  certificate: CertificateCustomer;
  refetchCertificates: () => void;
  selectMode?: boolean;
  selected?: boolean;
  onToggleSelected?: (id: string) => void;
  displayedData?: {
    product_name?: boolean;
    certificate_id?: boolean;
    menu_item?: boolean;
    last_activity?: boolean;
  };
}

export const CertificatesTeaser = ({
  certificate,
  refetchCertificates,
  selectMode = false,
  selected = false,
  onToggleSelected,
  displayedData,
  compact = false,
}: CertificatesTeaserProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { profile } = useSelector(({ auth }) => auth);

  const isCertificateOwner = isCertificateOwnedBy(certificate, profile?.id);
  const isShowMenuBase = true;
  const allowMenu = displayedData?.menu_item ?? true;
  const isShowMenu = isShowMenuBase && allowMenu;

  // Suppress `active`, and never print a raw enum. See
  // getCertificateStatusBadgeProps for why that logic lives in one place.
  //
  // This USED to be shared with the certificate page's ProductStatusBadge; that
  // component was removed in VRG-720 (an ownership status told an anonymous
  // scanner about the owner's activity), so the list is now the only consumer.
  // The reasoning does not carry over: a brand's own list is their own data,
  // seen by someone already signed in.
  const statusBadge = getCertificateStatusBadgeProps(certificate.status);

  /*
    DO TWO CARDS SHARE A ROW? — the one question two different decisions below
    both turn out to depend on, which is why it is named once rather than spelled
    out twice as the same expression.

    `compact` is the grid, and `certificateGridItemSpan` makes the grid one card
    per row at `xs` and multi-column from `sm` up. So a card has a neighbour
    beside it exactly when it is compact AND wide — never in the list, which is
    one card per row at every width.

    NOTE the hook is called unconditionally: `compact` flips at runtime when the
    user toggles the layout control, so `compact && useMediaQuery(...)` would be a
    conditional hook call.
  */
  const isWideCard = useMediaQuery(theme.breakpoints.up('sm'));
  const gridSharesRow = compact && isWideCard;

  /*
    THE ACTIVITY STRIP SHOWS ON EVERY CARD. No layout gate at all, where there
    were two in succession (`!compact`, then `!gridSharesRow`).

    VERA-315 dropped it from the grid because the activity block was the card's
    entire height variance: one card carried a transfer notice, its neighbour
    carried none, and the measured spread across a 2-up grid was 199px, falling
    to 28px once the block was gone. The measurement was right. What it measured
    no longer exists — the strip was a VARIABLE block then, growing with however
    many lines the notice ran to, and the description now clamps to one line, so
    it is 56px for a one-line row and 68 for a headline-plus-description one
    (`ACTIVITY_STRIP_MIN_H` carries that arithmetic). So a row's worst-case
    spread went from 199px to 68.

    AND it is off by default now (`DISPLAYED_DATA_DEFAULTS`), so the spread is
    opt-in: a list only pays it once someone has asked for activity.

    The remaining height is spent as dead space rather than as ragged edges, because
    the card is already `height: 100%` in a Grid whose items stretch, so every
    card in a row ends on the same line regardless. The strip takes
    `marginTop: auto` (see it below) so it sits at the BOTTOM of that stretched
    card — without which three cards in a row would show their activity at three
    different heights, which is the raggedness the original gate was actually
    avoiding.

    Dead space is a cosmetic cost and this file already accepts it explicitly
    (see the card's own note on why the photo must not grow into it). Hiding the
    most time-sensitive fact on the card — that it is mid-transfer — is not a
    cosmetic cost.
  */
  const activityAvailable = hasActivityToShow(certificate);
  /*
    The fallback reads `DISPLAYED_DATA_DEFAULTS` rather than restating `true`.
    It restated it, and the two then disagreed the moment the default flipped
    off: a card whose `displayedData` had not loaded yet showed activity that
    the popover said was hidden. One source, the same argument §36 makes for
    components, applied to a default.
  */
  const showActivity =
    (displayedData?.last_activity ?? DISPLAYED_DATA_DEFAULTS.last_activity) && activityAvailable;

  /*
    A CORNER question, not a height one — the photo fills the row either way (see the
    panel's `alignSelf`). When nothing follows, the row's bottom IS the card's bottom,
    so the photo's bottom-left takes the card's own curve; when the activity strip
    follows, the photo ends against a divider with card interior beneath it, and a
    curve there would bite into nothing.
  */
  const photoFillsCard = !compact && !showActivity;

  /*
    STATUS MOVES TO THE TITLE ROW'S RIGHT EDGE — grid only, and only once the card
    is wide enough to afford it.

    `compact` is the list-vs-grid discriminator, so gating on it is what keeps the
    LIST row's status exactly where it was (bottom-left of the text column, under
    the SKU). Nothing below touches the list path.

    But `compact` alone is not sufficient, and this is the measured part — it is
    the SAME question as `gridSharesRow` above, which is why this reuses it rather
    than restating the expression. A card only lacks the width for a badge in its
    title row when it is sharing that row's space with a neighbour.

    Measured on the 2-up card this was written against: a 163.5px shell whose
    title box is 137.5px, where "Louis Vuitton Monogram Jacket" already spends
    BOTH of its clamped lines ("Louis Vuitton" 93.6px, "Monogram Jacket" 129.4px).
    Handing 41.3px of badge plus an 8px gap to the same row leaves the title
    88.2px — less than "Louis Vuitton" alone, so the brand would wrap across both
    lines and the 2-line clamp would eat the product name outright.

    That 163.5px card is now the `sm` card rather than the phone card (grid went
    one-per-row at `xs`), and at `sm` the shell is ~276px, leaving the title
    >= 200px — both lines still fit whole. At `xs` the card is ~343px and the
    status stays in the text column, where the list has always put it.
  */
  /*
    The card title's line-height, in px. Declared here rather than inlined because the
    badge's optical offset below is arithmetic ON it — see that comment. The title
    itself sets `lineHeight='24px'` further down; if that ever changes, this must too,
    and a named constant is what makes the dependency findable.
  */
  const TITLE_LINE_HEIGHT = 24;

  /*
    The card's own border width, in px.

    It is the INSET every child flush against the card's inner edge has to
    subtract from the card's radius to stay concentric — see the photo panel
    below, and `edgeRadius`'s third argument for why.
  */
  const CARD_BORDER_PX = 1;


  /*
    THE BADGE RIDES THE TITLE ROW ON EVERY GRID CARD — `compact`, not
    `gridSharesRow`, so it no longer switches at the `sm` breakpoint.

    What the breakpoint bought and why it is not worth it: on a one-per-row card
    the badge used to drop to the SKU's line, which saved the same ~32px as the
    title row does (both avoid the badge taking a line of its own). So the choice
    was never height — it was WHICH line the badge shares.

    The SKU is the dimmest text on the card and the badge is the most salient
    thing on it, so pairing them read as a footnote about the SKU rather than the
    state of the item. Reported exactly that way. Sharing the title's line also
    removes a layout that changed with viewport width, which is one less thing
    for the next person to discover.

    ⚠️ The cost is real and lands on long titles: the title column gives up the
    badge's width, so a name that wrapped to two lines can now clip at the clamp.
    Measured at 375 before shipping — see the commit.

    STILL GRID ONLY. The list keeps the badge on its own line, and that part IS a
    measurement rather than a preference: the list's text column is narrower by
    the 128px photo plus its gap, its row height is floored by that photo so
    sharing a line saves ZERO, and the badge took enough width to truncate every
    SKU on screen ("LV-JACKET-...", where the distinguishing part is what goes).
    The list's title row also already carries the kebab, which the grid's does not
    — the grid's floats over the photo (VRG-774).
  */
  const statusInTitleRow = compact;

  /* VERA-628: `active` carries no information and is suppressed (matches the
      certificate page); "Opened" sits ALONGSIDE the ownership status, never
      replacing it — the two are orthogonal (an Active certificate can have a cut
      tamper loop). Not tappable here — the whole row already navigates to the
      certificate page, where the Opened sheet lives.

      Kept as ONE node used by both arrangements rather than duplicated per branch,
      so the badge set can never drift between them. */

  /*
    THE BADGE RIDES THE SKU'S ROW — it does not get a row of its own.

    Measured on the one-per-row grid card at 375: the info block under the image
    was 88px, and 40 of that (a 12px gap plus a 28px chip) was the badge's own
    line. The SKU beside it was using 115.1px of 317 available, so that line was
    carrying 202px of nothing while the badge paid full height for its own.
    Putting the badge at the SKU line's right edge spends the empty width instead
    of new height, with no element made smaller and nothing overlapping the photo.

    The saving is 32px, not the full 40: the SKU's line grows 20 -> 28 to hold the
    28px chip. So a badge-bearing card's info block goes 88 -> 56 (-36%) and the
    card 522 -> 490; a card with no badge is unchanged at 48, because it never
    paid for the extra line in the first place.

    ⚠️ GRID ONLY — and this was tried on the list card first, which is how the
    reason is a measurement rather than a preference. The list card's row is
    floored by its 128px photo, and the text column was already 88px, so sharing
    the SKU's line there saves exactly ZERO height. What it costs is real: the
    list's text column is narrower by the photo's 128px + gap, so the badge took
    enough of it to truncate every SKU on screen — "ROLEX-12...", "LV-JACKET-...",
    "CARTIER-L...". The existing clamp note calls the SKU "read by prefix, not a
    phrase", which survives "ROLEX-12..." but not "LV-JACKET-...", where the part
    that distinguishes one jacket from another is what goes.

    No height saved, legibility lost — so the list keeps the badge on its own
    line. Consistency between the two layouts is worth something, but not a
    regression on the layout that gains nothing for it.

    THREE arrangements now, not two, and the third is the reason this is a
    variable rather than an inline condition: the badge sits in the TITLE row on
    a shared-row grid card (`statusInTitleRow`), on the SKU row on a one-per-row
    GRID card that has a SKU, and on a line of its own everywhere else — every
    list card, and a grid card whose SKU is hidden by the Display menu's
    `certificate_id` toggle or absent from the product. That last pair is why the
    standalone placement survives rather than being deleted.
  */
  const showSku =
    (displayedData?.certificate_id ?? true) && Boolean(formatSku(certificate.product.sku));
  /*
    Now always false — `statusInTitleRow` is `compact`, so a grid card never
    reaches the SKU-row arrangement. Kept as a named constant rather than
    deleted because three call sites below branch on it, and collapsing them by
    hand is how one of them gets missed; the next person removing it can see at
    a glance that it is dead.
  */
  const statusOnSkuRow = compact && !statusInTitleRow && showSku;

  const statusRow = (
    <Stack
      direction='row'
      spacing={1}
      alignItems='center'
      /* `mt-3` is the gap for the STANDALONE line only. On the SKU row the Stack
         supplies the alignment and the row already has its own `mt-1`, so a
         second top margin here would push the badge below the SKU's baseline. */
      className={statusInTitleRow || statusOnSkuRow ? undefined : 'mt-3'}
      sx={
        statusInTitleRow
          ? {
              flexShrink: 0,
              /*
                `ml: auto` — not the title's flex-grow — is what pins the badges to
                the right edge. `displayedData.product_name` can hide the title
                entirely, and a lone status must still sit right rather than sliding
                back to the left where it would look like nothing had moved.
              */
              ml: 'auto',
              /*
                Optically centres the badge row on the title's FIRST 24px line.
                Without it the row aligns to the title BLOCK, so the badge would
                drift down by half a line the moment a title wrapped to two.
    
                VRG-511: DERIVED, not the old magic `2px`. That value centred the
                19.6px DOT row; the badge is now a 28px chip, and a stale +2 would
                push it 4px BELOW the line instead of 2px above centre. Written as
                the arithmetic so the next height change cannot silently invalidate
                it: (24 - 28) / 2 = -2.
              */
              mt: `${(TITLE_LINE_HEIGHT - STATUS_CHIP_HEIGHT) / 2}px`,
            }
          : undefined
      }
    >
      {statusBadge.show && (
        <StatusBadge
          status={certificate.status}
          label={statusBadge.label}
          /*
            STILL A DOT, deliberately. The corner placement was the one plausible
            reason to reach for `chip` again — a pill reads better against a busy
            edge — but the badge lands in the TEXT block on plain card background,
            not over the photo, so nothing about its legibility changed. The
            dot-over-pill decision (see StatusBadge's `variant` doc) was made
            because the chip's saturated fill out-shouted the product name; moving
            the badge to sit directly BESIDE that product name makes that argument
            stronger, not weaker.
          */
          /*
            VRG-511 — a TINTED CHIP, reversing the `dot` decision recorded in
            StatusBadge's `variant` doc.

            That decision was right about the problem and wrong about the remedy. Its
            reasoning: the chip's saturated FILL out-shouted the product name, and a
            dot keeps the colour signal at a fraction of the weight. The fill has
            since become a pale tint with a deep label, so the premise no longer
            holds — and the dot's own cost is now measured. Against the white card the
            dot's mid-tone renders at 1.21-1.46:1, i.e. it is very nearly invisible as
            a colour signal, leaving the reader to decode status from 14px secondary
            grey at 5.74:1. "Clean but hard to read" was exactly that: the colour
            channel carrying no information and the word carrying it faintly. The
            tinted chip puts the label at 8.20-13.55:1 AND makes the colour mean
            something.

            The loudness objection is answered by the TIER split rather than by
            shrinking the badge: `active` — 59% of rows — now renders nothing at all,
            so the pills that remain are the minority that says something.

            This is also the treatment the Status filter one tap away already uses for
            these same values, which is what made the dot a defect rather than merely
            a choice: one vocabulary, two treatments, same screen.
          */
          variant='chip'
          colors={statusBadge.colors}
          isCertificateOwner={isCertificateOwner}
        />
      )}
      {/*
        Follows the status badge into the chip grammar. Not part of the tier decision
        — an opened seal is orthogonal to ownership status (an Active certificate can
        have a cut tamper loop) — but a dot sitting beside a pill in the same row reads
        as unfinished, and this badge already carries NEUTRAL_RAMP so it needs no new
        colour. Flagged as a judgement call rather than an instruction.
      */}
      {certificate.is_tag_tampered && <TagOpenedBadge variant='chip' />}
    </Stack>
  );

  return (
    <Stack
      onClick={() => {
        /*
         * IN SELECT MODE THE WHOLE CARD TOGGLES. It does not navigate.
         *
         * Select mode is a mode: once you are in it, a tap means "select", and
         * every mobile app that has one works this way (Photos, Gmail, Files).
         * Requiring the 40px checkbox while the surrounding 343x112 card still
         * navigated was the worst of both — the small target was the only one
         * that did what the mode promises, and missing it did not just do
         * nothing, it left the screen entirely and discarded the selection
         * being built.
         */
        if (selectMode) {
          onToggleSelected?.(certificate.id);
          return;
        }
        // The detail page pushes in from the right on a phone; wider layouts get a
        // plain navigate (see utils/pageTransition). The decision is made HERE, at
        // the only entry point that has a previous screen to push away from — an
        // NFC tap lands on the detail page cold and must not animate.
        // Remember where the list is BEFORE leaving, so sliding back returns to
        // this card rather than to the top of a long list.
        saveScrollPosition('/certificates');
        const slideIn = shouldSlide();
        navigate(`/certificates/${certificate.id}`, { state: { slideIn } });
      }}
      sx={theme => ({
        position: 'relative',
        /*
         * FILL THE GRID CELL, so every card in a row ends on the same line.
         *
         * The ragged look was not the grid failing to reserve equal space — it
         * already did. MEASURED at 1280: every `Grid item` was 227px tall (a flex
         * row takes its tallest child), while the CARDS inside were 130px or
         * 211px depending on whether they carry the bottom activity strip. So a
         * card without one left an 81px void inside an already-correctly-sized
         * cell, and the bottoms disagreed by that much across a row.
         *
         * The whole variance is that strip (see `showActivity`): a bordered
         * footer block holding "Last scanned …" / "Transferred to …", present on
         * some certificates and not others.
         *
         * ## The cost, stated rather than hidden
         *
         * A card with no strip now carries that space as white below its content.
         * That is the trade being made, and it is the better half: a grid whose
         * cards start together and end together reads as a grid, where ragged
         * bottoms read as a rendering fault.
         *
         * ## Why the PHOTO is not stretched to absorb it
         *
         * The obvious follow-on — let the square photo grow into the extra height
         * — was built and MEASURED, and it is disqualified. The photo box became
         * 128x193, 128x128, 128x193, 128x143 across four cards: four different
         * crops of four products, and the shape is set by whatever else lands in
         * that ROW, so the same certificate would crop differently on a different
         * page of results. That destroys exactly the property the fixed 1:1 square
         * below was introduced to win ("every image identical … decoupled from
         * text length"). Dead space is a cosmetic cost; sibling-dependent cropping
         * is a correctness one.
         *
         * The deeper fix is to remove the variance at source — make the activity
         * strip a compact line inside the text column rather than a bordered
         * footer, which would cut the spread from ~81px to ~20px. That is a design
         * change to the card itself, not a layout fix, so it is not made here.
         */
        height: '100%',
        background: theme.palette.common.white,
        // VRG-562: the certificate card is the single most-seen surface in this
        // app and the one a brand looks at first when they change Corner radius.
        borderRadius: surfaceRadius(theme),
        border:
          selectMode && selected
            ? `1px solid ${theme.palette.primary.main}`
            : `1px solid ${theme.palette.grey[100]}`,
        // A white card on a white page was separated by a 1px hairline and nothing
        // else, which is what read as flat.
        //
        // The shadow — not the page tint — has to be what fixes this. Brands set
        // their own `bg_color` via Studio, and the seeded Louis Vuitton theme sets
        // it to `#ffffff` explicitly, so on a real brand's site the backdrop may
        // legitimately BE white and the softened page default never applies. An
        // elevation works on any backdrop the brand picks, which a tint cannot.
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.10), 0 1px 2px rgba(16, 24, 40, 0.06)',
        cursor: 'pointer',
        transition: 'box-shadow .15s ease, border-color .15s ease',
        '&:hover': {
          boxShadow: '0 2px 4px rgba(16, 24, 40, 0.06), 0 4px 8px rgba(16, 24, 40, 0.08)',
        },
      })}
    >
      {/*
        Row on a full-width card, COLUMN in the grid.
        
        A half-width card is ~180px, and the row layout spends 64 of that on the
        image plus 12 gap, leaving ~70px for a product title — which truncated
        "Omega Speedmaster Moonwatch" to "On S...". Stacking gives the title the
        card's full width and lets the image be the thing you actually scan by,
        which is the point of choosing a grid at all.
      */}
      <Stack
        direction={compact ? 'column' : 'row'}
        alignItems={compact ? 'stretch' : 'stretch'}
        sx={{
          // No padding on the leading edge in list mode: the photo BLEEDS to the
          // card's left and vertical edges rather than sitting inset on it, so
          // the card's edge IS the image. That is the single change that moves
          // this from "admin row with a thumbnail" to "object with a picture" —
          // an inset thumbnail always reads as metadata about the row, however
          // large you make it.
          /*
            VRG-720: the GRID photo bleeds now too, so neither layout insets it.

            The list's own note above is the argument, and it is stronger at 315px
            than at 128: "an inset thumbnail always reads as metadata about the row,
            however large you make it." A 12px frame around a photo that IS the card
            says the card is a container holding a picture; no frame says the card is
            the object. It also hands the photo the 24px the padding was taking —
            measured 315 -> 343 at a 375 viewport.

            The text block below now carries its own 12px, since this no longer
            supplies it.
          */
          padding: compact ? 0 : '0 12px 0 0',
          /*
            MINIMUM height, not a fixed one. Read both halves of this together —
            the minHeight on its own re-opens the bug the fixed height closed.

            The original problem: two cards with identical text-column content
            rendered at 104px and 156px, because the image container had
            `height: auto`, so the PHOTO'S intrinsic aspect ratio set the
            container height, which set the row height. A portrait shot made a
            tall card, a wide one a short card. Nothing to do with content. A
            fixed height fixed that — but it also capped CONTENT, and its stated
            budget (title 2x24 + 12 gap + 20 status + 32 padding = 112) never
            counted the SKU line. A card carrying the pending-transfer block
            needs ~152px, so it overflowed: first by crushing the title to 4px
            (see flexShrink on the title below), then, once that was fixed, by
            pushing the status chip 9px into the "Transfer out" heading.

            So: the row grows for CONTENT, and the image is stopped from
            contributing height by being taken out of flow (see the img below).
            Verified live at 375px — plain cards 112-116px, a card in a transfer
            state 156px, and a photo's shape no longer moves anything. Swapping
            to minHeight WITHOUT the img change was measured too, and the
            aspect-ratio bug came straight back (a Louis Vuitton card with no
            status chip went to 139px).
          */
          minHeight: compact ? undefined : '112px',
        }}
      >
        <Stack
          justifyContent='center'
          sx={{
            flexShrink: 0,
            // 104 and FULL-BLEED on the leading edge. The product image is the
            // emotional content of a list about things you own, and it kept being
            // sized like a favicon (48, then 64, then 88 inset).
            // 128, up from 104. The photo is the emotional content of a list about
            // things you own, and now that it is a fixed square (below) it no longer
            // borrows height from the text, so growing it costs nothing but its own
            // 24px. The row's 112px minHeight is now exceeded by the square itself,
            // which is what sets the card height — uniformly, for every card.
            width: compact ? '100%' : '128px',
            // `stretch` makes the photo a full-height panel rather than a square
            // floating in one — it fills whatever height the TEXT column asks for.
            height: compact ? 'auto' : 'auto',
            // Paired with the img's `position: absolute` below: together they are what
            // let the row above use minHeight instead of a fixed height. `stretch`
            // alone is not enough, because a stretched flex item still contributes its
            // intrinsic content height to the line's size — which is exactly how the
            // photo used to dictate the card height.
            //
            // Both are scoped to list mode. The grid path is deliberately untouched:
            // its tile is a fixed 1:1 square (`aspectRatio` above), so there is no
            // height for the photo to dictate and nothing here to fix.
            /*
              A FLOOR, not a size. The panel stretches to the row (see `alignSelf`),
              and a stretched item whose only child is out of flow has a content
              height of zero — so without this it would collapse to the row's 112px
              minHeight and the photo would SHRINK on exactly the sparse cards that
              already look emptiest. 128 keeps the square as the smallest it gets.
            */
            minHeight: compact ? undefined : '128px',
            position: compact ? undefined : 'relative',
            /*
              SQUARE in both modes, and top-aligned in list mode.

              It used to `stretch` to whatever height the TEXT column produced, so
              the photo was 104 x xisting-card-height — which meant a card with a
              two-line title and a wrapped SKU showed a noticeably taller image than
              the card above it. Every product photo was a different shape, which is
              the one thing a list of things you own should not do.

              Fixing the aspect ratio makes every image identical (104 x 104) and
              decouples image shape from text length entirely. `flex-start` is what
              stops the square being stretched back out by the flex line.
            */
            /*
              VRG-720 — THE PHOTO ALWAYS REACHES THE BOTTOM OF THE ROW. Where that
              bottom IS depends on whether an activity strip follows; the photo's
              height does not.

              `flex-start` + a 1:1 square was right while the square was the tallest
              thing in the row, which is what the 12px text padding below was tuned to
              make true. It stopped being true when the status DOT became a 28px chip
              (VRG-511): the text column now runs taller than 128 on any card carrying
              a status, and a flex-start square leaves the difference as a white band
              under the photo, INSIDE the card. MEASURED at 375 — a Lost card ran 40px
              of white between the photo and the divider, and a plain two-line card
              ~16px; the band is simply row height minus 128.

              So the square becomes a FLOOR (see minHeight) and the panel stretches.
              The height bug the fixed square was introduced to fix does not come back,
              because the img is `position: absolute` (see below) and contributes
              nothing: the photo now FOLLOWS the row's height instead of setting it.

              The cost, stated: a photo's crop is no longer identical card to card — it
              is 128 tall on a sparse card and ~148 on one carrying a chip. That is
              strictly weaker than the grid case this was disqualified for, where the
              shape was set by whatever ELSE landed in the row, so the same certificate
              cropped differently on a different page of results. Here it depends only
              on the card's own content.
            */
            alignSelf: 'stretch',
            aspectRatio: compact ? '1 / 1' : undefined,
            marginRight: compact ? 0 : '16px',
            marginBottom: compact ? '10px' : 0,
            // No border in EITHER mode now — a hairline around a full-bleed panel
            // just draws a box back around the thing we stopped putting in a box,
            // and as of this change the grid panel is full-bleed as well.
            border: 'none',
            // Outer corners follow the card; inner edge stays square so the
            // bleed reads as continuous with the card rather than as a rounded
            // tile pushed against it.
            /*
              VRG-562: both follow the tenant's Corner radius, at different steps.

              Grid mode: the photo is a tile NESTED in the card, so it takes the
              inner step (concentric radii decrease inward).

              List mode: TOP-LEFT ONLY. It was `12px 0 0 12px`, which rounded the
              bottom-left too — and that corner is not a corner. `alignSelf` is
              `flex-start` here, so the panel is a square that stops short of the
              card's bottom edge: MEASURED at 375x812, the photo's bottom sits
              82-90px above the card's, with the activity strip in between. The
              bottom-left curve therefore bit into the card's own interior with
              nothing behind it. At the old fixed 12px that read as a slight
              softening; driven to the top of the brand range it is unmistakable,
              which is how the extremes pass caught a geometry error that had been
              shipping for as long as the literal had.
            */
            /*
              Grid mode WAS `innerRadius` — the inner step of a concentric pair,
              correct while the tile was nested inside a 12px frame. Bleeding it
              makes the photo's top corners the CARD's corners, so they take the
              card's own step; the bottom two stay square against the text block.

              MINUS the card's 1px border, which is the whole point of the third
              argument. The card curves at 12 on its BORDER box, so its padding
              box — the hole this panel fills — curves at 11. Asking for 12 made
              the photo's arc rounder than the hole, so it pulled off the corner
              and the card's white showed through as a wedge. Visible on both
              layouts; the list had it from the day it started bleeding.
            */
            borderRadius: compact
              ? edgeRadius(surfacePx, [1, 1, 0, 0], CARD_BORDER_PX)
              : edgeRadius(
                  surfacePx,
                  photoFillsCard ? [1, 0, 0, 1] : [1, 0, 0, 0],
                  CARD_BORDER_PX
                ),
            overflow: 'hidden',
            /*
              The panel carries the placeholder's own fill even when there IS a
              photo, so the photo has something to fade up over rather than a
              white hole. Same grey the no-image branch below uses, so a list
              mixing both reads as one material while it loads.
            */
            backgroundColor: theme => theme.palette.grey[50],
          }}
        >
          {certificate.product.images && certificate.product.images.length > 0 ? (
            <FadeInImage
              src={certificate.product.images[0]}
              alt='certificate image'
              style={
                compact
                  ? {
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      // NO radius. The panel's `overflow: hidden` clips this, as
                      // both of these branches already conceded ("which clips it
                      // anyway") — and a second, DIFFERENT value is how a corner
                      // silently stops matching: this one asked for `innerPx` (8)
                      // inside a panel at 11.

                    }
                  : {
                      // OUT OF FLOW in list mode. An in-flow img reports its intrinsic
                      // height to the flex layout, so a portrait photo grew the card and
                      // a landscape one shrank it — the "some cards are bigger than
                      // others" bug. Absolutely positioned, it contributes nothing to
                      // sizing and simply fills whatever box the text column produced,
                      // cropping via objectFit. This is what makes the row's minHeight
                      // safe. The grid (compact) path keeps the in-flow img: there the
                      // tile is a fixed 1:1 square, so there is no height to dictate.
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      // NO radius — the panel clips it (see the grid branch).
                    }
              }
            />
          ) : (
            /*
              The SHARED empty state (@repo/ui), not a local copy of a generic
              photo glyph. It carries a PRODUCT glyph — a picture-frame icon says
              "an image is missing", which is true and useless; the product glyph
              says what the missing thing is OF.

              `aspectRatio` stays here rather than moving into the placeholder:
              the geometry constraint is this card's, not the component's. The
              placeholder must obey the SAME box a real photo fills, or a card
              with no image ignores the 104x112 panel and renders a ~200px square
              — which was the last source of "why are some cards bigger than
              others". Square only in the grid, where the tile IS square.
            */
            <ProductImagePlaceholder
              glyphSize={compact ? 40 : 28}
              sx={{ aspectRatio: compact ? '1 / 1' : undefined }}
            />
          )}
        </Stack>
        <Box
          sx={{
            // The row's padding moved to the image side, so the text column
            // carries its own.
            /*
              12, not 16 — and this is what actually closes the gap under the photo.

              The photo is a fixed 128 square, the row is as tall as its TALLEST
              column, and the text column is content + 2x this padding. Measured on a
              two-line-title card: 104 content + 32 padding = 136, i.e. 8px taller
              than the photo, so the photo stopped 8px short of the divider with the
              text column setting the row height. At 12 the same card computes
              104 + 24 = 128 — exactly the photo — so the PHOTO drives the row and its
              bottom edge meets the divider.

              The SKU clamp earlier got the content down far enough for this to work;
              on its own it was not sufficient, because the padding was always going
              to add 32.
            */
            paddingY: compact ? 0 : '12px',
            /*
              The grid card's own 12px, now that the Stack above bleeds. It is
              horizontal + bottom only: the TOP gap to the photo is the panel's
              `marginBottom: 10px`, left alone so the measured 10 does not silently
              become 22.
            */
            paddingX: compact ? '12px' : 0,
            paddingBottom: compact ? '12px' : 0,
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
          }}
        >
          {(() => {
            const titleEl = (displayedData?.product_name ?? true) && (
              <Typography
                fontSize='16px'
                fontWeight='500'
                lineHeight='24px'
                sx={{
                  // `auto` in title-row mode: a 100%-wide flex item would claim the
                  // whole row and push the badges out of the card.
                  width: statusInTitleRow ? 'auto' : '100%',
                  flex: statusInTitleRow ? '1 1 auto' : undefined,
                  minWidth: statusInTitleRow ? 0 : undefined,
                  /*
                    The 2-line cap, enforced by HEIGHT in title-row mode.

                    The clamp below is unreliable here for the reason the flexShrink
                    note explains — the browser blockifies `-webkit-box` away — and in
                    the column arrangement what actually trims the text is `overflow`
                    against the leftover flex height. In a ROW with align-items
                    flex-start there IS no leftover height to trim against: the title
                    takes its natural size, so a long name would run to three or four
                    lines and grow the card past its neighbours. 48px = 2 x the 24px
                    lineHeight, restoring the cap explicitly.
                  */
                  maxHeight: statusInTitleRow ? '48px' : undefined,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  // VERA-304: WITHOUT THIS THE PRODUCT NAME CAN VANISH ENTIRELY.
                  //
                  // This Typography is a flex item (the column Box above), so the browser
                  // BLOCKIFIES `-webkit-box` away — computed display comes back `flow-root`,
                  // and with it the line-clamp stops applying. What actually trims the text
                  // is then the `overflow: hidden` above, against whatever height the flex
                  // row has left over. That is fine while the card is only an image + title
                  // + SKU, which is why it read as working: the leftovers happened to be
                  // ~2 lines.
                  //
                  // A card carrying the pending-transfer block ("Transferred to X, ownership
                  // is not claimed yet") has no leftovers, and the title collapsed to 4.4px
                  // of a 48px content box -- measured, not estimated. The name disappeared
                  // and the card showed only a SKU. Every gifted item hits this, because a
                  // gift is exactly what puts a certificate into `transfer_initiated`.
                  //
                  // flexShrink 0 makes the title keep its own height and pushes the trimming
                  // back onto the clamp, where it belongs: a long name loses its THIRD line,
                  // never its first.
                  //
                  // ONLY in the column arrangement. flexShrink is resolved along the
                  // container's MAIN axis, so in the column it protects HEIGHT (what the
                  // paragraph above is about) but in the title ROW it would protect WIDTH
                  // — the title would refuse to give the badges any room and would shove
                  // them off the card's right edge instead.
                  flexShrink: statusInTitleRow ? 1 : 0,
                }}
              >
                {certificate.product.title}
              </Typography>
            );

            /*
              The row exists whenever the status belongs in it, even if the title is
              hidden — `statusRow` has to have somewhere to live, and `ml: auto` keeps
              it right-aligned with or without a sibling.
            */
            return statusInTitleRow ? (
              <Stack
                direction='row'
                alignItems='flex-start'
                // Explicit px, not a spacing factor: apps/app is on MUI's 8px base
                // while @repo/ui is on 4px, so a factor copied between them doubles.
                sx={{ width: '100%', gap: '8px' }}
              >
                {titleEl}
                {statusRow}
              </Stack>
            ) : (
              titleEl
            );
          })()}
          {/*
            The SKU row, which the status badge rides (see `statusOnSkuRow`).

            `justifyContent: space-between` + a `gap`, NOT `spacing` + `ml: auto`
            on the badge. Measured: with `spacing={1}` the badge rendered hard
            against the SKU instead of at the card's right edge, because Stack
            implements spacing as a DESCENDANT selector on the parent
            (`& > :not(style) ~ :not(style) { margin-left }`), which is more
            specific than the child's own single-class `sx` and silently wins. The
            same shape as §31's label-gap trap: a Stack's spacing beats what the
            child asked for, so the parent has to be the one doing the placing.

            `gap` is the MINIMUM separation, for the case where a long SKU and a
            wide badge meet in the middle; `space-between` does the rest.

            `width: 100%` is load-bearing and is the second thing that had to be
            measured: the text column is `flex-direction: column` with
            `align-items: start`, so this row was sized to its CONTENT (185px of
            the column's 317) and `space-between` had no free space to distribute
            — the badge sat flush against the SKU looking like a deliberate pair.
            Stretching the row to the column is what creates the gap it divides.
          */}
          {showSku && (
            <Stack
              direction='row'
              alignItems='center'
              className='mt-1'
              sx={{ width: '100%', gap: '8px', justifyContent: 'space-between' }}
            >
            <Typography
              fontSize='14px'
              fontWeight='400'
              lineHeight='20px'
              color={theme.palette.text.secondary}
              sx={{
                /*
                  ONE line. This is what closes the gap between the photo and the
                  activity row.

                  The photo is a fixed 128 square and the top block is as tall as its
                  TALLEST column, so whenever the text ran longer than 128 the extra
                  height appeared as dead space beside — and below — the photo, with
                  the divider pushed away from it. A wrapped SKU
                  ("RAYBAN-WAYFARER-RB2140" going to two lines) was the usual culprit,
                  and it is the least worth wrapping: it is a machine identifier read
                  by prefix, not a phrase. Title (2 lines) + SKU (1) + status (1)
                  ~= 98px, comfortably inside the photo's 128, so the PHOTO sets the
                  block height on every card and the divider sits directly under it.
                */
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 1,
                overflow: 'hidden',
                wordBreak: 'break-all',
                /* The SKU is the one that YIELDS. It already clamps to a line and
                   breaks anywhere, so it can absorb a wide badge ("Cancel request")
                   by truncating — but only with `minWidth: 0`, since a flex item
                   defaults to `min-width: auto` and would otherwise refuse to shrink
                   and shove the badge off the card's right edge instead. */
                minWidth: 0,
              }}
            >
              {formatSku(certificate.product.sku)}
            </Typography>
              {statusOnSkuRow && (
                <Box sx={{ ml: 'auto', flexShrink: 0, display: 'flex' }}>{statusRow}</Box>
              )}
            </Stack>
          )}

          {/* A line of its OWN — the LIST card always (see the grid-only note on
              `statusOnSkuRow`), and the grid card when there is no SKU row to ride.
              In title-row mode this renders nothing here because `statusRow` is
              already mounted above. */}
          {!statusInTitleRow && !statusOnSkuRow && statusRow}
        </Box>
        {selectMode ? (
          <Box
            onClick={e => e.stopPropagation()}
            /*
              Inset from BOTH edges it is near. The wrapper only carried `ml: 1`,
              so with the row's own 12px right padding the box sat ~12px from the
              right and hard against the top of a 112px row — visibly tighter than
              anything else on the card. 16/16 puts it on the same inset as the
              card's other corner content.
            */
            sx={{ flexShrink: 0, ml: 1, mr: '4px', mt: '16px', alignSelf: 'flex-start' }}
          >
            {/*
              The card itself now toggles in select mode, so this must NOT also
              handle the tap — the click bubbles to the card, and two toggles
              for one tap cancel out, making the checkbox the one place in the
              card that appears to do nothing. Stopping propagation and letting
              the card own the toggle keeps a single handler: the checkbox is
              the affordance, the whole card is the target.
            */}
            <SelectCheckbox
              checked={selected}
              onChange={() => onToggleSelected && onToggleSelected(certificate.id)}
              onClick={(event: React.MouseEvent) => event.stopPropagation()}
            />
          </Box>
        ) : isShowMenu ? (
          /*
            In the grid the card is a COLUMN, so leaving the kebab in flow put it
            on its own row under the title — a full row of height spent on one
            icon, on the layout chosen to fit more per screen. Floated over the
            image's top-right instead, which is where every grid card in the
            reference apps puts its overflow.
          */
          <Box
            onClick={e => e.stopPropagation()}
            sx={
              compact
                ? { position: 'absolute', top: 18, right: 18, zIndex: 2 }
                : // The negative margins were pulling the kebab OUT of the card:
                  // they existed to cancel the row's old 16px top/right padding,
                  // and the full-bleed change removed that padding, so they had
                  // nothing left to cancel. Positive inset instead, aligned to
                  // the top of the text column.
                  { flexShrink: 0, ml: 0.5, mt: '12px' }
            }
          >
            <CertificatesMenu
              certificate={certificate}
              buttonTopPosition='0px'
              buttonRightPosition='0px'
            />
          </Box>
        ) : null}
      </Stack>
      {/*
        MOUNTED whenever there is activity to show, and COLLAPSED when the
        Display popover has it off — rather than mounted only when shown.

        The toggle is the whole reason: a conditional mount has no intermediate
        state, so the strip and its divider appeared and vanished between two
        frames, which at the bottom of a card reads as the card snapping to a
        different size rather than as a section opening. `Collapse` animates the
        height it already has to measure, so the card grows into it.

        `timeout="auto"` matches `AnchoredPanel` and MUI's own `Menu`: the
        duration comes from the measured height, so a one-line "Last scanned"
        row and a three-line transfer notice each take the time their own
        distance deserves instead of sharing one number.

        NOT `appear`, and that is the opposite of `AnchoredPanel`'s choice for a
        reason. A Transition with no `appear` mounts straight into its end
        state, which here is exactly right — a list of twenty cards should not
        play twenty expansions on load. The animation belongs to the TOGGLE, and
        the toggle is the only thing that changes `in` after mount.

        `unmountOnExit` is deliberately off: `Collapse` needs the content in the
        DOM to measure what it is animating to.
      */}
      {activityAvailable && (
        <Collapse
          in={showActivity}
          timeout='auto'
          /*
            Pushed to the BOTTOM of the card. The card is `height: 100%` in a
            Grid whose items stretch, so on a shared row it is as tall as its
            tallest sibling — and without this the strip floats wherever the
            content happens to end, so three cards in a row show their activity
            at three different heights.

            It sits on the COLLAPSE, not on the divider as it used to: the
            divider is no longer a flex child of the card (the Collapse is), and
            an `auto` margin on a non-flex-child does nothing at all.
          */
          sx={{ marginTop: 'auto' }}
        >
          {/*
            The inset MATCHES THE CARD'S OWN CONTENT INSET, which differs by
            layout: the compact card pads its contents 12px all round, the list
            card 16px. The strip is a SIBLING of that padded Stack, not a child,
            so it does not inherit either and has to say which it is following —
            left at a flat 16 it put the divider 4px inside the image above it on
            the grid card, which reads as a misalignment rather than an inset.
          */}
          <Box
            sx={theme => ({
              borderTop: `1px solid ${theme.palette.grey[200]}`,
              mx: compact ? '12px' : 2,
              my: 0,
            })}
          />
          {/*
            12px, not 16 — see `ACTIVITY_STRIP_MIN_H`. The strip has to come in
            UNDER the title/SKU block it sits beneath, and the padding is half of
            the two numbers that decide that.
          */}
          <Stack
            sx={{ px: compact ? '12px' : 2, py: '12px', minHeight: ACTIVITY_STRIP_MIN_H }}
          >
            <StatusNotification {...certificate} refetchCertificates={refetchCertificates} />
          </Stack>
        </Collapse>
      )}
    </Stack>
  );
};
